import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CohortSegment } from "@/types/cohort.types";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, Clock, ExternalLink } from "lucide-react";

interface CohortSegmentComparisonProps {
  segments: CohortSegment[];
  isLoading?: boolean;
  segmentType: string;
}

export const CohortSegmentComparison = ({ segments, isLoading, segmentType }: CohortSegmentComparisonProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Segment Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-[140px]" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSegmentIcon = (name: string): string => {
    if (name.toLowerCase().includes('zahnarzt')) return '🦷';
    if (name.toLowerCase().includes('hausarzt')) return '🏥';
    if (name.toLowerCase().includes('facharzt')) return '⚕️';
    if (name.toLowerCase().includes('mvz')) return '🏢';
    if (name.toLowerCase().includes('klinik')) return '🏨';
    if (name.toLowerCase().includes('desktop')) return '🖥️';
    if (name.toLowerCase().includes('mobile')) return '📱';
    if (name.toLowerCase().includes('tablet')) return '📱';
    if (name.toLowerCase().includes('organic')) return '🔍';
    if (name.toLowerCase().includes('direct')) return '➡️';
    if (name.toLowerCase().includes('social')) return '👥';
    return '📊';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>📊 Segment Performance</CardTitle>
        <CardDescription>
          Vergleich der {segmentType === 'practice_type' ? 'Praxistypen' : segmentType === 'source' ? 'Traffic-Quellen' : 'Gerätetypen'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {segments.map((segment) => (
            <Card key={segment.value} className="overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="text-2xl">{getSegmentIcon(segment.name)}</span>
                  <span>{segment.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Conversion Rate</span>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-green-500" />
                    <span className="font-semibold text-lg">
                      {segment.conversionRate.toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Anzahl</span>
                  <span className="text-sm font-medium">{segment.count}</span>
                </div>

                {segment.avgTimeToConversion !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Ø Zeit
                    </span>
                    <span className="text-sm font-medium">
                      {segment.avgTimeToConversion.toFixed(1)} Tage
                    </span>
                  </div>
                )}

                {segment.topEntryPage && (
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />
                      Top Einstieg
                    </span>
                    <span className="text-xs font-medium truncate max-w-[150px]">
                      {segment.topEntryPage}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {segments.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            Keine Segment-Daten verfügbar
          </div>
        )}
      </CardContent>
    </Card>
  );
};
